// src/context/LanguageContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import en from "../locales/en.json";
import ka from "../locales/ka.json";

type Language = "en" | "ka";

// Allow nested objects
type NestedString = string | { [key: string]: NestedString };
type Translations = Record<string, NestedString>;

const translations: Record<Language, Translations> = { en, ka };

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("language") as Language;
    return saved === "en" || saved === "ka" ? saved : "en";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  // Helper to get nested value by dot notation (e.g., "admin.dashboard.heading")
  const getNestedValue = (obj: any, path: string): string => {
    const keys = path.split(".");
    let current = obj;
    for (const key of keys) {
      if (current === undefined || current === null) return path;
      current = current[key];
    }
    return typeof current === "string" ? current : path;
  };

  const t = (key: string): string => {
    const translation = translations[language];
    return getNestedValue(translation, key);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};